import { Box, Button, useTheme } from "@mui/material";
import { FC } from "react";

import { ChatHelper } from "../../helpers/chatHelper";
import { forceDownloadFile } from "../../../services/fileDownloadService";
import { DownloadIcon } from "../../icons";
import FileItemMenu from "./FileItemMenu";
import { IFileAttachment } from "../../../types/chat";

const DOWNLOAD_BUTTON_SX = {
  display: "inline-flex",
  alignItems: "center",
  color: "text.primary",
  height: "25px",
  minWidth: 0,
} as const;

/** Keeps each row's "..." menu button hidden until the row is hovered/focused,
 * except while its own menu is open (it would otherwise vanish once the
 * pointer leaves the row for the open menu). */
const FILE_ITEM_MENU_VISIBILITY_SX = {
  "& .file-item-menu-button": { opacity: 0 },
  "&:hover .file-item-menu-button, &:focus-within .file-item-menu-button, & .file-item-menu-button.menu-open":
    { opacity: 1 },
} as const;

export const getOriginalFileName = (file: IFileAttachment, index: number) =>
  file.original_name || file.file_name || file.name || `File ${index + 1}`;

interface FileRowProps {
  file: IFileAttachment;
  index: number;
  canDownloadAttachments: boolean;
  isClosed: boolean;
  onSelect: (file: IFileAttachment, originalFileName: string) => void;
  onTogglePin: (file: IFileAttachment) => void;
  onRename: (file: IFileAttachment, currentName: string) => void;
}

/** Row for a single attached file in {@link SidebarFileViewer}. */
const FileRow: FC<FileRowProps> = ({
  file,
  index,
  canDownloadAttachments,
  isClosed,
  onSelect,
  onTogglePin,
  onRename,
}) => {
  const theme = useTheme();
  const originalFileName = getOriginalFileName(file, index);
  const displayName = file.display_name || originalFileName

  return (
    <Box
      onClick={() => onSelect(file, originalFileName)}
      title={`Click to edit: ${displayName}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: 1,
        py: 0.5,
        borderRadius: 1,
        cursor: "pointer",
        "&:hover": {
          bgcolor: "grey.50",
          textDecoration: "underline",
        },
        ...FILE_ITEM_MENU_VISIBILITY_SX,
      }}
    >
      {canDownloadAttachments ? (
        <Button
          variant="text"
          size="small"
          aria-label={`Download ${displayName}`}
          title="Download file"
          onClick={async (e) => {
            e.stopPropagation();
            let fileId = (file.id as string | null) ?? null;
            if (!fileId) {
              const fileUrl = file.file_url || file.url;
              fileId = ChatHelper.extractFileIdFromUrl(fileUrl as string);
            }
            if (fileId) {
              await forceDownloadFile(fileId, displayName);
            } else {
              ChatHelper.downloadFile(file);
            }
          }}
          sx={{
            ...DOWNLOAD_BUTTON_SX,
            "&:hover": {
              color: theme.palette.custom.brandTeal,
              backgroundColor: "transparent",
              "& svg path": { fill: "currentColor" },
            },
          }}
        >
          <DownloadIcon size={13} className="text-inherit" />
        </Button>
      ) : null}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          color: "#2D2D2D",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: "0.875rem",
          fontWeight: 500,
        }}
      >
        {displayName}
      </Box>
      {!isClosed && (
        <FileItemMenu
          isPinned={!!file.is_pinned}
          ariaLabel={`More options for ${displayName}`}
          onTogglePin={() => onTogglePin(file)}
          onRename={() => onRename(file, displayName || originalFileName)}
        />
      )}
    </Box>
  );
};

export default FileRow;
